/**
 * DocumentDetailModal Component
 *
 * Modal for viewing document details and downloading.
 * Features:
 * - Fetches document detail for metadata
 * - Fetches document blob via proxy endpoint for preview/download
 * - Shows document metadata (title, type, size, created date)
 * - PDF preview using PdfViewer component
 * - Image preview
 */

import React, { useCallback, useEffect } from 'react';
import { PdfViewer } from '@components/PdfViewer';
import {
  Close as CloseIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useLazyGetDocumentProxyBlobQuery,useLazyGetDocumentQuery } from '../store/api';
import { useDocumentBlobUrl } from '../store/hooks';

import { DOCUMENT_TYPE_OPTIONS } from './DocumentFilters';

// Format file size to human readable
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Format date to localized string with time
const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get document type label
const getDocTypeLabel = (type: string | null | undefined): string => {
  if (!type) return 'Unknown';
  const option = DOCUMENT_TYPE_OPTIONS.find(opt => opt.value === type);
  return option?.label ?? type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

interface DocumentDetailModalProps {
  open: boolean;
  documentId: string | null;
  onClose: () => void;
}

export const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  open,
  documentId,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Fetch document metadata (title, type, size, etc.)
  const [fetchDocument, { data: document, isLoading: isLoadingMeta, isFetching, error }] =
    useLazyGetDocumentQuery();

  // Fetch document blob via proxy for preview/download
  const { blobUrl, isLoading: isLoadingBlob, error: blobError, fetchDocument: fetchBlob, clearBlobUrl } =
    useDocumentBlobUrl({ disposition: 'inline' });

  // Lazy hook for download (attachment disposition)
  const [fetchDownloadBlob] = useLazyGetDocumentProxyBlobQuery();

  // Fetch both metadata and blob when modal opens
  useEffect(() => {
    if (open && documentId) {
      void fetchDocument({ documentId });
      void fetchBlob(documentId);
    }
    return () => {
      clearBlobUrl(); // Cleanup blob URL on close
    };
  }, [open, documentId, fetchDocument, fetchBlob, clearBlobUrl]);

  // Handle download for non-PDF files
  const handleDownload = useCallback(async () => {
    if (!documentId || !document) return;
    
    try {
      const blob = await fetchDownloadBlob({ documentId, disposition: 'attachment' }).unwrap();
      const downloadBlobUrl = URL.createObjectURL(blob);
      
      const link = window.document.createElement('a');
      link.href = downloadBlobUrl;
      link.download = document.filename || `${document.document_title || 'document'}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(downloadBlobUrl), 100);
    } catch (error) {
      console.error('Download error:', error);
    }
  }, [documentId, document, fetchDownloadBlob]);

  const isPdf = document?.content_type === 'application/pdf';
  const isImage = document?.content_type?.startsWith('image/');
  const isLoading = isLoadingMeta || isLoadingBlob;
  const canPreview = document && (isPdf || isImage) && blobUrl;
  const hasError = error || blobError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          height: fullScreen ? '100%' : '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocumentIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 600 }} noWrap>
          {document?.document_title || document?.filename || 'Document Details'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : hasError ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              Failed to load document. Please try again.
            </Alert>
          </Box>
        ) : document ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Document Metadata - Compact Header */}
            <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', flexShrink: 0 }}>
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  label={getDocTypeLabel(document.document_type)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(document.size_bytes)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(document.created_at)}
                </Typography>
              </Stack>
            </Box>

            <Divider />

            {/* Preview Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: 'grey.100' }}>
              {canPreview && blobUrl ? (
                isPdf ? (
                  <PdfViewer
                    url={blobUrl}
                    filename={document.filename}
                    height="100%"
                    eventMetadata={{
                      documentType: document.document_type || undefined,
                      patientId: document.patient_id || undefined,
                      documentId: document.document_id,
                    }}
                  />
                ) : isImage ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Box
                      component="img"
                      src={blobUrl}
                      alt={document.document_title || document.filename}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 1,
                        boxShadow: 2,
                      }}
                    />
                  </Box>
                ) : null
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    py: 6,
                  }}
                >
                  <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary" gutterBottom>
                    Preview not available for this file type
                  </Typography>
                  {document && !isPdf && (
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      sx={{ mt: 2 }}
                    >
                      Download File
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDetailModal;
